import { z } from 'zod';

export const SCORING_STRATEGY_HEURISTIC_V0 = 'heuristic_v0' as const;
/** Heuristic v0 blended with the LLM rubric judge (PRE-14). */
export const SCORING_STRATEGY_HEURISTIC_PLUS_LLM = 'heuristic_plus_llm_rubric' as const;

export const SCORING_STRATEGIES = [
  SCORING_STRATEGY_HEURISTIC_V0,
  SCORING_STRATEGY_HEURISTIC_PLUS_LLM,
] as const;
export const scoringStrategySchema = z.enum(SCORING_STRATEGIES);
export type ScoringStrategy = z.infer<typeof scoringStrategySchema>;

/**
 * POST /v1/feedback body — a single answer to score.
 *
 * Matches the per-item signals the FR-4 design collects: answer, duration,
 * hint count and self-reported confidence (1-5).
 */
export const scoreAnswerInputSchema = z.object({
  answer: z.string(),
  expectedConcepts: z.array(z.string()).default([]),
  hintCount: z.number().int().min(0).default(0),
  /** Time spent on the item, in milliseconds. */
  durationMs: z.number().int().min(0).default(0),
  /** Self-reported confidence, 1-5 (optional). */
  confidence: z.number().int().min(1).max(5).optional(),
  questionId: z.string().min(1).optional(),
  sessionId: z.string().min(1).optional(),
});
export type ScoreAnswerInput = z.infer<typeof scoreAnswerInputSchema>;

export const scoringPrechecksSchema = z.object({
  emptyAnswer: z.boolean(),
  tooShort: z.boolean(),
  hasCode: z.boolean(),
});
export type ScoringPrechecks = z.infer<typeof scoringPrechecksSchema>;

export const scoringSignalsSchema = z.object({
  conceptMatch: z.number(),
  matchedConcepts: z.array(z.string()),
  hintCountPenalty: z.number(),
  durationSignal: z.number(),
  confidenceMismatch: z.number(),
});
export type ScoringSignals = z.infer<typeof scoringSignalsSchema>;

/**
 * Aggregate scores on a 0-100 scale (FR-4 §6).
 * `ai_dependency_score` is a penalty accumulation: higher = more dependent.
 */
export const scoringScoresSchema = z.object({
  concept_mastery_score: z.number(),
  explanation_score: z.number(),
  ai_dependency_score: z.number(),
  total_score: z.number(),
});
export type ScoringScores = z.infer<typeof scoringScoresSchema>;

/**
 * LLM rubric judge output (PRE-14). Each criterion is scored on its 0-5 scale
 * with a one-line justification; `overall_score` is a model-calibrated 0-100
 * holistic quality signal that the aggregator blends with the heuristic scores.
 */
export const rubricCriterionResultSchema = z.object({
  key: z.string(),
  score: z.number().int().min(0).max(5),
  justification: z.string(),
});
export type RubricCriterionResult = z.infer<typeof rubricCriterionResultSchema>;

export const rubricJudgementSchema = z.object({
  criteria: z.array(rubricCriterionResultSchema),
  overall_score: z.number().int().min(0).max(100),
  strengths: z.array(z.string()),
  weaknesses: z.array(z.string()),
  feedback: z.string(),
});
export type RubricJudgement = z.infer<typeof rubricJudgementSchema>;

/** The full scoring result plus the persisted report identity. */
export const feedbackReportSchema = z.object({
  id: z.string(),
  userId: z.string(),
  questionId: z.string().nullable(),
  sessionId: z.string().nullable(),
  scoringStrategy: scoringStrategySchema,
  scores: scoringScoresSchema,
  prechecks: scoringPrechecksSchema,
  signals: scoringSignalsSchema,
  strengths: z.array(z.string()),
  weaknesses: z.array(z.string()),
  feedbackText: z.string(),
  /** LLM rubric judgement when the LLM judge ran; null for heuristic-only. */
  rubric: rubricJudgementSchema.nullable().default(null),
  createdAt: z.string(),
});
export type FeedbackReportResponse = z.infer<typeof feedbackReportSchema>;
